import { useEffect, useState, useCallback } from 'react';
import { fetchMissingTimestampsKPI } from '../api/missingTimestampsApi';

export const useMissingTimestampsKpi = (activeFile = null, options = {}) => {
  const { batchData } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Consolidated data loading logic
  const loadData = useCallback(async (fileType = null) => {
    setLoading(true);
    setError(null);
    try {
      const effectiveFileType = fileType ?? activeFile ?? null;

      // Check if we have batch data available (preferred for performance)
      if (batchData && typeof batchData === 'object' && batchData.count_missing_timestamps) {
        console.info('useMissingTimestampsKpi: Using batch data for missing timestamps KPI');
        setData(batchData.count_missing_timestamps);
        return;
      }

      // Fallback to individual API call if no batch data available
      console.info('useMissingTimestampsKpi: Falling back to individual API call');
      const result = await fetchMissingTimestampsKPI(effectiveFileType);
      setData(result);
    } catch (e) {
      const msg = String(e?.message || e || '');
      // Suppress 404-like errors to render graceful empty state instead of error tile
      if (/Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg)) {
        setData({ file_type: fileType || activeFile || 'main', total_events: 0, invalid_timestamp_events: 0, invalid_ratio: 0 });
        setError(null);
      } else {
        setError(e?.message || 'Failed to load missing timestamps KPI');
      }
    } finally {
      setLoading(false);
    }
  }, [activeFile, batchData]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // Priority: use batch data if available
      if (batchData && typeof batchData === 'object' && batchData.count_missing_timestamps) {
        console.info('useMissingTimestampsKpi: Using batch data for missing timestamps KPI');
        setLoading(false);
        if (!cancelled) setData(batchData.count_missing_timestamps);
        return;
      }

      // If batch data exists but doesn't have count_missing_timestamps yet, wait for it (batch might still be loading)
      if (batchData && typeof batchData === 'object' && batchData.count_missing_timestamps === undefined) {
        console.info('useMissingTimestampsKpi: Batch data exists but count_missing_timestamps not ready yet, waiting...');
        // Return without setting loading state - this will retry on next batchData change
        return;
      }

      // Fallback: individual API call only if batch data is not available at all or explicitly null
      console.info('useMissingTimestampsKpi: Batch data not available, using individual API call');
      setLoading(true);
      setError(null);
      try {
        const result = await fetchMissingTimestampsKPI(activeFile ?? null);
        if (!cancelled) setData(result);
      } catch (e) {
        if (!cancelled) {
          const msg = String(e?.message || e || '');
          if (/Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg)) {
            setData({ file_type: activeFile || 'main', total_events: 0, invalid_timestamp_events: 0, invalid_ratio: 0 });
            setError(null);
          } else {
            setError(e?.message || 'Failed to load missing timestamps KPI');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => { cancelled = true; };
  }, [activeFile, batchData]);

  return { data, loading, error, refresh: loadData };
};
